import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Calendar, ArrowRight, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT_HEADING = "'Cormorant Garamond', Cambria, Georgia, serif";
const FONT_BODY = "'Jost', sans-serif";

// Defensive: turn any raw slug value from the DB (which may contain spaces,
// /blog/ prefixes, or URL-encoded chars from bad admin entries) into a clean slug.
const cleanSlug = (raw) => {
  if (!raw) return '';
  let s = String(raw).trim();
  try { s = decodeURIComponent(s); } catch { /* keep as-is */ }
  s = s.trim().replace(/^\/+/, '');
  if (s.toLowerCase().startsWith('blog/')) s = s.slice(5);
  s = s.trim().replace(/^\/+|\/+$/g, '');
  if (s.includes('/')) s = s.split('/').pop();
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
};

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${API}/blog/posts`);
        if (res.ok) setPosts(await res.json());
      } catch {}
      setLoading(false);
    };
    fetchPosts();
  }, []);

  return (
    <div className="min-h-screen bg-[#1A1A1A]" data-testid="blog-page">
      <Navbar />
      <div className="pt-20">
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-4" style={{ fontFamily: FONT_BODY }}>Insights & Analysis</p>
        <h1 className="text-4xl md:text-5xl text-[#FAF8F5] mb-16 tracking-tight" style={{ fontFamily: FONT_HEADING }}>The Sàwai Capital Blog</h1>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 border border-[#A0743A]/10">
            <p className="text-[#999] text-lg" style={{ fontFamily: FONT_BODY }}>No articles published yet. Check back soon.</p>
          </div>
        ) : (
          <div className="space-y-12" data-testid="blog-posts-list">
            {posts.map(post => {
              const slug = cleanSlug(post.slug);
              return (
              <article key={post.post_id} className="border-b border-[#A0743A]/10 pb-12 group" data-testid={`blog-post-${slug}`}>
                {post.cover_image && (
                  <Link to={`/blog/${slug}`} className="block max-w-2xl" data-testid={`blog-post-image-link-${slug}`}>
                    <img src={post.cover_image} alt={post.title} className="w-full h-auto mb-6 opacity-90 group-hover:opacity-100 transition-opacity cursor-pointer" />
                  </Link>
                )}
                <div className="flex items-center gap-4 mb-4">
                  {post.tags?.map(tag => (
                    <span key={tag} className="text-[#A0743A] text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1" style={{ fontFamily: FONT_BODY }}>
                      <Tag size={10} /> {tag}
                    </span>
                  ))}
                </div>
                <Link to={`/blog/${slug}`}>
                  <h2 className="text-2xl md:text-3xl text-[#FAF8F5] mb-3 hover:text-[#A0743A] transition-colors tracking-tight" style={{ fontFamily: FONT_HEADING }}>{post.title}</h2>
                </Link>
                {post.excerpt && <p className="text-[#999] text-base leading-relaxed mb-4" style={{ fontFamily: FONT_BODY }}>{post.excerpt}</p>}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-[#555] text-xs" style={{ fontFamily: FONT_BODY }}>
                    <Calendar size={12} />
                    {post.published_at ? new Date(post.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                  </div>
                  <Link to={`/blog/${slug}`} className="flex items-center gap-2 text-[#A0743A] text-xs uppercase tracking-wider font-semibold hover:text-[#C9A96E] transition-colors" style={{ fontFamily: FONT_BODY }}>
                    Read More <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
              );
            })}
          </div>
        )}
      </main>
      </div>
      <Footer />
    </div>
  );
}
